import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, Paragraph } from '@/components/atoms';
import { MessageMediaContent } from '@/components/molecules/message-media-content';
import { CHAT_REACTION_EMOJIS } from '@/lib/constants';
import { CheckCheckIcon, ClockIcon, RotateCcwIcon, XIcon } from 'lucide-react';
import type { MessageMediaType, MessageReaction } from '@/types';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/atoms';

export interface MessageBubbleProps {
  sender: string;
  message: string;
  time?: string;
  isOutgoing: boolean;
  deliveryStatus?: 'pending' | 'sent' | 'failed';
  isFirstInGroup?: boolean;
  isLastInGroup?: boolean;
  showAvatar?: boolean;
  mediaType?: MessageMediaType;
  mediaUrl?: string;
  fileName?: string;
  mimeType?: string;
  fileSize?: number;
  uploadProgress?: number;
  reactions?: MessageReaction[];
  onReact?: (emoji: string) => void;
  onRetry?: () => void;
  className?: string;
}

function MessageBubble({
  sender,
  message,
  time,
  isOutgoing,
  deliveryStatus,
  isFirstInGroup = true,
  isLastInGroup = true,
  showAvatar = true,
  mediaType = 'text',
  mediaUrl,
  fileName,
  mimeType,
  fileSize,
  uploadProgress,
  reactions,
  onReact,
  onRetry,
  className,
  ...props
}: MessageBubbleProps) {
  const StatusIcon =
    isOutgoing && deliveryStatus
      ? deliveryStatus === 'pending'
        ? ClockIcon
        : deliveryStatus === 'sent'
          ? CheckCheckIcon
          : XIcon
      : null;

  const hasMedia = mediaType !== 'text' && !!mediaUrl;
  const isPlaceholderLabel =
    message === '[Image]' ||
    message === '[Video]' ||
    message === '[Audio]' ||
    message === '[Voice message]' ||
    message === '[Sticker]' ||
    message.startsWith('[Document');
  const isRedundantFileName =
    hasMedia &&
    (mediaType === 'document' || mediaType === 'file') &&
    !!fileName &&
    message.trim() === fileName.trim();
  const showText =
    !!message && (!hasMedia || (!isPlaceholderLabel && !isRedundantFileName));

  return (
    <div
      className={cn(
        'group flex',
        isOutgoing ? 'justify-end' : 'justify-start',
        className
      )}
      {...props}
    >
      <div
        className={cn(
          'flex max-w-xs gap-3 lg:max-w-md',
          isOutgoing ? 'flex-row-reverse' : 'flex-row'
        )}
      >
        {showAvatar ? (
          <Avatar className="size-12">
            <AvatarFallback className="bg-green-600 text-lg text-white">
              {sender}
            </AvatarFallback>
          </Avatar>
        ) : (
          <div className="w-12" />
        )}
        <div className="min-w-0">
          <div
            className={cn(
              'mb-2 px-4 py-4',
              'rounded-lg',
              isFirstInGroup && isOutgoing && 'rounded-br-none',
              isFirstInGroup && !isOutgoing && 'rounded-bl-none',
              isLastInGroup && isOutgoing && 'rounded-tr-none',
              isLastInGroup && !isOutgoing && 'rounded-tl-none',
              isOutgoing
                ? 'border border-green-600 bg-transparent text-gray-900'
                : 'bg-green-600 text-white'
            )}
          >
            {hasMedia && (
              <MessageMediaContent
                mediaType={mediaType}
                mediaUrl={mediaUrl}
                fileName={fileName}
                mimeType={mimeType}
                fileSize={fileSize}
                caption={showText ? message : undefined}
                isOutgoing={isOutgoing}
                uploadProgress={uploadProgress}
              />
            )}
            {showText && !hasMedia && (
              <Paragraph size="sm" className="whitespace-pre-wrap break-words">
                {message}
              </Paragraph>
            )}
            {showText && hasMedia && (
              <Paragraph
                size="sm"
                className="mt-2 whitespace-pre-wrap break-words"
              >
                {message}
              </Paragraph>
            )}
            {(time || StatusIcon) && (
              <div
                className={cn(
                  'mt-1 flex items-center gap-1.5 text-xs',
                  isOutgoing ? 'text-gray-500' : 'text-green-100'
                )}
              >
                {time && <span>{time}</span>}
                {StatusIcon && (
                  <StatusIcon
                    className={cn(
                      'h-3.5 w-3.5',
                      deliveryStatus === 'failed' && isOutgoing
                        ? 'text-red-500'
                        : undefined
                    )}
                  />
                )}
              </div>
            )}
          </div>

          {deliveryStatus === 'failed' && isOutgoing && onRetry && (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="mb-1 h-auto gap-1 p-0 text-xs text-destructive"
              onClick={onRetry}
            >
              <RotateCcwIcon className="size-3" />
              Retry upload
            </Button>
          )}

          {reactions && reactions.length > 0 && (
            <div className="mb-1 flex flex-wrap gap-1">
              {reactions.map((r, i) => (
                <span
                  key={`${r.emoji}-${i}`}
                  className="rounded-full border bg-white px-2 py-0.5 text-sm shadow-sm"
                >
                  {r.emoji}
                </span>
              ))}
            </div>
          )}

          {onReact && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="mb-2 text-xs text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"
                >
                  React
                </button>
              </PopoverTrigger>
              <PopoverContent side="top" className="w-auto p-1">
                <div className="flex gap-0.5">
                  {CHAT_REACTION_EMOJIS.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      className="flex size-8 items-center justify-center rounded-md text-lg hover:bg-gray-100"
                      onClick={() => onReact(emoji)}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>
    </div>
  );
}

export { MessageBubble };
